import { useState } from "react";
import { useAtomValue } from "jotai";
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Button from "@mui/material/Button";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useLzFShowErrorMessage } from "@lz-services/Utils";
import lzSAuthAppConfigsAtom from "@lz-store/atoms/Auth/appConfigs";
import LzXAutocompleteField from "@lz-components/forms/fields/AutocompleteField";
import LzXInputField from "@lz-components/forms/fields/InputField";
import PartyModel from "modules/Params/Parties/party.model";
import SalesInvoiceModel from "./sales-invoice.model";
import SalesInvoiceDetailModel from "./sales-invoice-detail.model";

const SalesInvoiceDetailsComponent = (props) => {
    const authAppConfigsState = useAtomValue(lzSAuthAppConfigsAtom);

    const [showBulkEntry, setShowBulkEntry] = useState(false);
    const [bulkEntryText, setBulkEntryText] = useState('');
    const [showItemSearch, setShowItemSearch] = useState(-1);

    const lzFShowErrorMessage = useLzFShowErrorMessage();

    const handleBulkEntry = () => {
        const rows = bulkEntryText.split("\n");
        // console.log('Rows', rows);
        if (rows.length < 1) {
            lzFShowErrorMessage('No rows provided to add.');
            return;
        }

        const updated = new SalesInvoiceModel(Object.assign({}, props.data));

        let rno = 0;
        for (const row of rows) {
            rno++;

            const cols = row.split("\t");
            // console.log('Cols for Row #', rno, cols);
            if (cols.length <= 1) {
                continue; // skip empty rows
            } else {
                if (cols.length < 8) {
                    lzFShowErrorMessage('Row # ' + rno + ': Not enough data columns were found (' + cols.length + ' should be atleast 8).');
                    return;
                }
            }

            const sno = +cols[0];
            // console.log('Row #', rno, 'S#', sno);
            if (sno < 1) {
                lzFShowErrorMessage('Row # ' + rno + ': S# is empty.');
                return;
            }
            const itemCode = cols[1].trim();
            // console.log('Row #', rno, 'ItemCode', itemCode);
            if ('' === itemCode) {
                lzFShowErrorMessage('Row # ' + rno + ': Item Code is empty.');
                return;
            }
            const itemCategory = cols[2].trim();
            // console.log('Row #', rno, 'ItemCategory', itemCategory);
            if ('' === itemCategory) {
                lzFShowErrorMessage('Row # ' + rno + ': Item Category is empty.');
                return;
            }
            const itemSize = cols[3].trim();
            // console.log('Row #', rno, 'ItemSize', itemSize);
            if ('' === itemSize) {
                lzFShowErrorMessage('Row # ' + rno + ': Item Size is empty.');
                return;
            }
            const itemQuality = cols[4].trim();
            // console.log('Row #', rno, 'ItemQuality', itemQuality);
            if ('' === itemQuality) {
                lzFShowErrorMessage('Row # ' + rno + ': Item Quality is empty.');
                return;
            }
            const weightKg = +cols[5];
            // console.log('Row #', rno, 'ItemWeightKg', weightKg);
            if (weightKg <= 0) {
                lzFShowErrorMessage('Row # ' + rno + ': Item Weight is empty.');
                return;
            }
            const qty = +cols[6];
            // console.log('Row #', rno, 'ItemQty', qty);
            if (qty < 1) {
                lzFShowErrorMessage('Row # ' + rno + ': Item Quantity is empty.');
                return;
            }
            const price = +cols[7];
            // console.log('Row #', rno, 'ItemPrice', price);
            if (price < 1) {
                lzFShowErrorMessage('Row # ' + rno + ': Item Price is empty.');
                return;
            }
            const cost = cols.length > 8 ? +cols[8] : qty * price;
            // console.log('Row #', rno, 'ItemCost', cost, qty * price, cols.length > 8);
            if (cost !== qty * price) {
                lzFShowErrorMessage('Row # ' + rno + ': Item Cost (' + cost + ') does not match calculated cost (' + (qty * price) + ').');
                return;
            }

            updated.sales_invoice_details.push(
                new SalesInvoiceDetailModel(
                    {
                        sno: sno,
                        item_code: itemCode,
                        item_type_name: itemCategory,
                        item_size_name: itemSize,
                        item_quality_name: itemQuality,
                        quantity: qty,
                        weight: weightKg,
                        price: price,
                    },
                    updated.discount_rate,
                    updated.sales_tax_rate,
                    updated.further_tax_rate,
                    updated.is_buyer_registered
                )
            );
        }

        setShowBulkEntry(false);
        setBulkEntryText('');
        props.onChange(updated);
    };

    const handleInputChange = (dex, key, val) => {
        const updated = new SalesInvoiceModel(props.data);
        updated.sales_invoice_details[dex] = new SalesInvoiceDetailModel(
            updated.sales_invoice_details[dex],
            updated.discount_rate,
            updated.sales_tax_rate,
            updated.further_tax_rate,
            updated.is_buyer_registered
        );
        if ('item_id' == key) {
            updated.sales_invoice_details[dex].item_hs_code = val.hs_code;
            updated.sales_invoice_details[dex].item_code = val.code;
            updated.sales_invoice_details[dex].item_type_name = val.type_name;
            updated.sales_invoice_details[dex].item_size_name = val.size_name;
            updated.sales_invoice_details[dex].item_quality_name = val.quality_name;
            updated.sales_invoice_details[dex].price = +val.default_price;
        } else {
            updated.sales_invoice_details[dex][key] = val;
        }
        props.onChange(updated);
        setShowItemSearch(-1);
    };

    const handleDelete = (dex) => {
        const updated = new SalesInvoiceModel(props.data);
        updated.sales_invoice_details.splice(dex, 1);
        props.onChange(updated);
        setShowItemSearch(-1);
    };

    const handleAddDetail = () => {
        const updated = new SalesInvoiceModel(Object.assign({}, props.data));
        updated.sales_invoice_details.push(
            new SalesInvoiceDetailModel(
                {
                    sno: props.data.sales_invoice_details.reduce((maxSno, currItem) => Math.max(maxSno, currItem.sno), 0) + 1,
                    item_hs_code: authAppConfigsState?.misc?.default_item_hs_code || '',
                },
                props.data.discount_rate,
                props.data.sales_tax_rate,
                props.data.further_tax_rate,
                props.data.is_buyer_registered
            )
        );
        props.onChange(updated);
    };

    return (
        <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', right: 0 }}>
                <Button variant="text" size="small" color="primary" onClick={() => setShowBulkEntry(true)}>Bulk Entry</Button>
            </div>
            <h3 style={{ marginBottom: 12 }}>Enter Sales Invoice Items Detail</h3>
            <TableContainer component={Paper}>
                <Table size="small" padding="normal" className="lz-sales-invoice-details-grid">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '3%', textAlign: 'center' }} className="general-column">S#</TableCell>
                            <TableCell sx={{ width: showItemSearch >= 0 ? '15%' : '3%' }} className="item-column">Item</TableCell>
                            {/* <TableCell sx={{ width: '8%' }} className="item-column">HS Code</TableCell> */}
                            {/* <TableCell sx={{ width: '5%' }} className="item-column">Code</TableCell> */}
                            <TableCell sx={{ width: '11%' }} className="item-column">Category</TableCell>
                            <TableCell sx={{ width: '11%' }} className="item-column">Size</TableCell>
                            <TableCell sx={{ width: '9%' }} className="item-column">Quality</TableCell>
                            <TableCell sx={{ width: '5%', whiteSpace: 'nowrap' }} className="unit-column">Wt.Kg</TableCell>
                            <TableCell sx={{ width: '4%', whiteSpace: 'nowrap' }} className="unit-column">Qty</TableCell>
                            <TableCell sx={{ width: '7%' }} className="cost-column">Price</TableCell>
                            <TableCell sx={{ width: '7%' }} className="cost-column">Gross&nbsp;Sales</TableCell>
                            <TableCell sx={{ width: '7%' }} className="cost-column">Discount</TableCell>
                            <TableCell sx={{ width: '7%' }} className="cost-column">Before&nbsp;Tax</TableCell>
                            <TableCell sx={{ width: '7%' }} className="cost-column">Sales&nbsp;Tax</TableCell>
                            {!props.data.is_buyer_registered && <TableCell sx={{ width: '7%' }} className="cost-column">Further&nbsp;Tax</TableCell>}
                            <TableCell sx={{ width: '1%' }} className="general-column">
                                <IconButton size="small" color="success" onClick={handleAddDetail}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {props.data.sales_invoice_details &&
                        <TableBody>
                            {props.data.sales_invoice_details.map((sinvd, sinvdex) => (
                                <TableRow key={'sinvd-' + sinvdex}>
                                    <TableCell className="general-column">
                                        <LzXInputField fluid compact variant="outlined" size="small" label="" name="sno"
                                            value={sinvd.sno} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                        />
                                    </TableCell>
                                    <TableCell className="item-column">
                                        {showItemSearch === (sinvdex + 1) ?
                                            <LzXAutocompleteField fluid compact lazyLoad variant="outlined" size="small"
                                                placeholder="type to search..." label="" refTableName="items" name="item_id" style={{ marginTop: 4 }}
                                                value={sinvd.item_id} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                                onBlur={() => setShowItemSearch(-1)}
                                                refFilterFieldKeys={['hs_code', 'code', 'type_name', 'size_name', 'quality_name', 'default_price']}
                                            />
                                            :
                                            <IconButton size="small" onClick={() => setShowItemSearch(sinvdex + 1)}>
                                                <SearchIcon fontSize="small" />
                                            </IconButton>
                                        }
                                    </TableCell>
                                    {/* <TableCell className="item-column">
                                        <LzXInputField fluid compact variant="outlined" size="small" label="" name="item_hs_code"
                                            value={sinvd.item_hs_code} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                        />
                                    </TableCell> */}
                                    {/* <TableCell className="item-column">
                                        <LzXInputField fluid compact variant="outlined" size="small" label="" name="item_code"
                                            value={sinvd.item_code} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                        />
                                    </TableCell> */}
                                    <TableCell className="item-column">
                                        <LzXInputField fluid compact variant="outlined" size="small" label="" name="item_type_name"
                                            value={sinvd.item_type_name} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                        />
                                    </TableCell>
                                    <TableCell className="item-column">
                                        <LzXInputField fluid compact variant="outlined" size="small" label="" name="item_size_name"
                                            value={sinvd.item_size_name} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                        />
                                    </TableCell>
                                    <TableCell className="item-column">
                                        <LzXInputField fluid compact variant="outlined" size="small" label="" name="item_quality_name"
                                            value={sinvd.item_quality_name} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                        />
                                    </TableCell>
                                    <TableCell className="unit-column">
                                        <LzXInputField fluid compact variant="outlined" size="small" label="" name="weight"
                                            value={sinvd.weight} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                        />
                                    </TableCell>
                                    <TableCell className="unit-column">
                                        <LzXInputField fluid compact variant="outlined" size="small" label="" name="quantity"
                                            value={sinvd.quantity} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                        />
                                    </TableCell>
                                    <TableCell className="cost-column">
                                        <LzXInputField fluid compact variant="outlined" size="small" label="" name="price"
                                            value={sinvd.price} onChange={(name, value) => handleInputChange(sinvdex, name, value)}
                                        />
                                    </TableCell>
                                    <TableCell className="cost-column">
                                        <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={sinvd.amount} />
                                    </TableCell>
                                    <TableCell className="cost-column">
                                        <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={sinvd.discount} />
                                    </TableCell>
                                    <TableCell className="cost-column">
                                        <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={sinvd.gross} />
                                    </TableCell>
                                    <TableCell className="cost-column">
                                        <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={sinvd.sales_tax} />
                                    </TableCell>
                                    {!props.data.is_buyer_registered &&
                                        <TableCell className="cost-column">
                                            <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={sinvd.further_tax} />
                                        </TableCell>
                                    }
                                    <TableCell className="general-column">
                                        <IconButton size="small" color="error" onClick={() => handleDelete(sinvdex)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow className="totals-row">
                                <TableCell className="general-column">
                                    <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={props.data.sales_invoice_details.length} />
                                </TableCell>
                                <TableCell colSpan={4} className="item-column" style={{ textAlign: 'right', fontWeight: 500, textTransform: 'uppercase' }}>Totals : &nbsp;</TableCell>
                                <TableCell className="unit-column">
                                    <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={props.data.total_weight} />
                                </TableCell>
                                <TableCell className="unit-column">
                                    <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={props.data.total_quantity} />
                                </TableCell>
                                <TableCell className="cost-column"></TableCell>
                                <TableCell className="cost-column">
                                    <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={props.data.total_amount} />
                                </TableCell>
                                <TableCell className="cost-column">
                                    <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={props.data.total_discount} />
                                </TableCell>
                                <TableCell className="cost-column">
                                    <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={props.data.total_gross} />
                                </TableCell>
                                <TableCell className="cost-column">
                                    <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={props.data.total_sales_tax} />
                                </TableCell>
                                {!props.data.is_buyer_registered &&
                                    <TableCell className="cost-column">
                                        <LzXInputField fluid compact disabled variant="outlined" size="small" label="" value={props.data.total_further_tax} />
                                    </TableCell>
                                }
                                <TableCell className="general-column"></TableCell>
                            </TableRow>
                        </TableBody>
                    }
                </Table>
            </TableContainer>

            {showBulkEntry &&
                <Dialog open fullWidth maxWidth="lg" onClose={() => setShowBulkEntry(false)}>
                    <DialogTitle>Enter Item Details in Bulk</DialogTitle>
                    <DialogContent>

                        <Typography variant="body1" style={{ fontWeight: "bold" }}>Instructions:</Typography>
                        <Typography variant="caption">
                            - Use the following example format to enter items data in bulk.<br />
                            - No header is required, what is shown below is just for clarification.<br />
                            - Items are separated by newline.<br />
                            - Columns are separated by a single TAB character.<br />
                        </Typography>

                        <pre style={{ fontSize: 12 }}>
                            &lt;sno&gt;      &lt;item-code&gt;     &lt;item-category&gt;             &lt;item-size&gt;            &lt;item-quality&gt;    &lt;weight-kg&gt;   &lt;qty-pcs&gt;  &lt;price-per-pc&gt;  &lt;optional-total&gt;<br />
                            1	          10311           Grinding Wheel Straight     6 x 3/4 x 1            CCG 46 K V        11.200        16         1991	           31856<br />
                            2	          10312           Grinding Wheel Straight     6 x 3/4 x 1            CCG 60 K V        7.100         10         1991	           19910<br />
                            3	          10313           Grinding Wheel Straight     8 x 1 x 1              CCG 36 K V        27.200        16         3396	           54336<br />
                            4	          10314           Grinding Wheel Straight     12 x 1.1/2 x 1.1/2     EL 46 N V         36.780        6          4716            28296<br />
                        </pre>
                        <br />

                        <TextField fullWidth multiline name="bulkentrytxt" variant="outlined"
                            minRows={8} size="small" label="Enter text here as instructed above"
                            value={bulkEntryText} onChange={(evt) => setBulkEntryText(evt.target.value)}
                            slotProps={{
                                htmlInput: { style: { fontSize: 13, lineHeight: 1.1 } },
                                inputLabel: { shrink: true },
                            }}
                        />

                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleBulkEntry} size="small" color="primary" variant="contained">Process</Button>
                        <Button onClick={() => setBulkEntryText('')} size="small">Reset</Button>
                        <Button onClick={() => setShowBulkEntry(false)} size="small">Close</Button>
                    </DialogActions>
                </Dialog>
            }

        </div>
    );

};

export default SalesInvoiceDetailsComponent;
